<div class="domain-setup-section">
  <div class="card p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm mb-4">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom border-slate-200 dark:border-slate-700">
      <div>
        <h4 class="text-xl font-bold text-slate-800 dark:text-gray-100 mb-1">
          {{ "Custom Domain Setup" | translate }}
        </h4>
        <p class="text-sm text-slate-500 dark:text-gray-400 mb-0">
          {{ "Connect and manage custom domains and subdomains for your store." | translate }}
        </p>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
          [disabled]="isDomainListLoading"
          (click)="refreshDomains(true)"
          title="{{ 'Refresh Domains' | translate }}"
        >
          <i class="fi fi-rr-refresh mr-1.5" [class.fa-spin]="isDomainListLoading"></i>
          <span>{{ "Refresh" | translate }}</span>
        </button>
        <button class="btn btn-primary btn-sm d-inline-flex align-items-center" (click)="openAddDomainModal()">
          <i class="fi fi-rr-plus mr-1.5"></i>
          <span>{{ "Create Domain" | translate }}</span>
        </button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div *ngIf="isDomainListLoading" class="text-center py-5">
      <mat-spinner class="mx-auto my-4" [diameter]="40"></mat-spinner>
      <p class="text-sm text-slate-400 mb-0">{{ "Loading domains..." | translate }}</p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isDomainListLoading && (!domainList || domainList.length === 0)" class="text-center py-5">
      <div class="mb-3">
        <i class="fi fi-rr-globe text-4xl text-slate-300 dark:text-slate-600"></i>
      </div>
      <h5 class="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">
        {{ "No Custom Domains Added" | translate }}
      </h5>
      <p class="text-sm text-slate-400 max-w-md mx-auto mb-4">
        {{ "You haven't connected any custom domain to your store yet. Click below to add a domain." | translate }}
      </p>
      <div class="d-flex align-items-center justify-content-center gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
          [disabled]="isDomainListLoading"
          (click)="refreshDomains(true)"
        >
          <i class="fi fi-rr-refresh mr-1.5" [class.fa-spin]="isDomainListLoading"></i>
          <span>{{ "Refresh" | translate }}</span>
        </button>
        <button class="btn btn-primary btn-sm d-inline-flex align-items-center" (click)="openAddDomainModal()">
          <i class="fi fi-rr-plus mr-1.5"></i>
          <span>{{ "Create Domain" | translate }}</span>
        </button>
      </div>
    </div>

    <!-- Domain Table Listing -->
    <div *ngIf="!isDomainListLoading && domainList && domainList.length > 0" class="table-responsive">
      <table class="table align-middle mb-0">
        <thead class="bg-slate-50 dark:bg-slate-900/50">
          <tr>
            <th class="text-start py-3 px-3 text-xs font-semibold uppercase text-slate-500" scope="col">{{ "Domain & Status" | translate }}</th>
            <th class="text-start py-3 px-3 text-xs font-semibold uppercase text-slate-500" scope="col">{{ "Connected Subdomains" | translate }}</th>
            <th class="text-end py-3 px-3 text-xs font-semibold uppercase text-slate-500" scope="col">{{ "Action" | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let data of domainList" class="border-b border-slate-100 dark:border-slate-700/50">
            <td class="text-start py-3 px-3">
              <div class="d-flex align-items-center gap-2 mb-1">
                <div class="domain-icon-wrap rounded-md bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 d-flex align-items-center justify-content-center flex-shrink-0">
                  <i class="fi fi-rr-globe text-xs"></i>
                </div>
                <span class="font-semibold text-sm text-slate-800 dark:text-slate-100 font-mono">{{ data.domain_name }}</span>
                <span class="badge bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 text-[10px] font-normal py-0.5 px-2 rounded">
                  {{ (data.is_existing_domain ? "Existing" : "New") | translate }}
                </span>
              </div>

              <!-- Compact Status Tag directly under domain name -->
              <div class="domain-status-row">
                <!-- Active -->
                <span *ngIf="+data.status === 2" class="domain-status-indicator status-active">
                  <span class="domain-status-dot dot-emerald"></span>
                  <span>{{ "Active" | translate }}</span>
                </span>

                <!-- Deploying -->
                <span *ngIf="+data.status === 1" class="domain-status-indicator status-deploying">
                  <span class="domain-status-dot dot-sky animate-pulse-dot"></span>
                  <span>{{ "Deploying" | translate }}</span>
                  <span class="text-[11px] text-slate-400" *ngIf="+data.setup_progress < domainFinalStep">({{ data.setup_progress }}/{{ domainFinalStep }})</span>
                </span>

                <!-- Action Required -->
                <button
                  type="button"
                  *ngIf="+data.status === 1 && +data.is_waiting_user_input === 1"
                  class="btn-domain-action-badge"
                  title="{{ 'Action Required - Click to complete step' | translate }}"
                  (click)="showUserInput(data)"
                >
                  <i class="fi fi-rs-exclamation"></i>
                  <span>{{ "Take Action" | translate }}</span>
                </button>

                <!-- Failed -->
                <span *ngIf="+data.status === 3" class="domain-status-indicator status-failed">
                  <span class="domain-status-dot dot-red"></span>
                  <span>{{ "Setup Failed" | translate }}</span>
                </span>

                <!-- Deleting -->
                <span *ngIf="+data.status === 8" class="domain-status-indicator status-deleting">
                  <span class="domain-status-dot dot-orange"></span>
                  <span>{{ "Deleting..." | translate }}</span>
                </span>

                <!-- Deleted -->
                <span *ngIf="+data.status === 9" class="domain-status-indicator status-deleted">
                  <span class="domain-status-dot dot-slate"></span>
                  <span>{{ "Deleted" | translate }}</span>
                </span>

                <!-- Disabled / Other -->
                <span *ngIf="+data.status === 0" class="domain-status-indicator status-disabled">
                  <span class="domain-status-dot dot-slate"></span>
                  <span>{{ "Disabled" | translate }}</span>
                </span>
              </div>
            </td>
            <td class="text-start py-3 px-3 text-slate-600 dark:text-slate-300">
              <ng-container *ngIf="getSubdomainList(data).length; else noSubdomains">
                <div class="flex flex-wrap gap-1.5">
                  <ng-container *ngFor="let sub of getSubdomainList(data)">
                    <a
                      *ngIf="isClickableSubdomain(sub) && !isDeletingDomain(data)"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800 text-xs text-decoration-none"
                      [href]="buildSubdomainUrl(sub, data.domain_name)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>{{ sub }}</span>
                      <i class="fi fi-rr-arrow-small-right text-[9px]"></i>
                    </a>
                    <span
                      *ngIf="!isClickableSubdomain(sub) || isDeletingDomain(data)"
                      class="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-600 text-xs"
                    >
                      {{ sub }}
                    </span>
                  </ng-container>
                </div>
              </ng-container>
              <ng-template #noSubdomains>
                <span class="text-slate-400 text-xs italic">{{ "No subdomains" | translate }}</span>
              </ng-template>
            </td>
            <td class="text-end py-3 px-3">
              <div class="d-inline-flex align-items-center gap-1.5 flex-nowrap">
                <button
                  type="button"
                  *ngIf="+data.status === 1 && +data.is_waiting_user_input === 1"
                  class="btn-domain-action btn-domain-action-amber font-semibold"
                  title="{{ 'Action Required - Complete Setup Step' | translate }}"
                  (click)="showUserInput(data)"
                >
                  <i class="fi fi-rs-exclamation"></i>
                  <span>{{ "Take Action" | translate }}</span>
                </button>
                <button
                  type="button"
                  *ngIf="+data.setup_progress === 22 || +data.setup_progress === 23"
                  class="btn-domain-action btn-domain-action-indigo"
                  [disabled]="isDeletingDomain(data)"
                  [title]="(isDeletingDomain(data) ? ('Domain is being deleted' | translate) : ('Manage Subdomains' | translate))"
                  (click)="openManageSubdomains(data)"
                >
                  <i class="fi fi-rs-settings"></i>
                  <span>{{ "Subdomains" | translate }}</span>
                </button>
                <button
                  type="button"
                  class="btn-domain-action btn-domain-action-slate"
                  title="{{ 'View Details' | translate }}"
                  (click)="showDomainDetails(data)"
                >
                  <i class="fi fi-rs-info"></i>
                  <span>{{ "Details" | translate }}</span>
                </button>
                <button
                  type="button"
                  class="btn-domain-action btn-domain-action-red"
                  [disabled]="isDeletingDomain(data)"
                  [title]="(isDeletingDomain(data) ? ('Domain is being deleted' | translate) : ('Remove Domain' | translate))"
                  (click)="deleteDomain(data)"
                >
                  <span *ngIf="isDeletingDomain(data)" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                  <i class="fi fi-rs-trash" *ngIf="!isDeletingDomain(data)"></i>
                  <span>{{ (isDeletingDomain(data) ? "Deleting..." : "Remove") | translate }}</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>

<!-- Details Modal (Revamped & Modern) -->
<ng-template #domainDetailsModal let-modal>
  <div class="modal-header border-bottom border-slate-100 dark:border-slate-700 pb-3">
    <div class="d-flex align-items-center gap-3">
      <div class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 d-flex align-items-center justify-content-center flex-shrink-0">
        <i class="fi fi-rr-globe text-base"></i>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <h5 class="modal-title font-bold text-base text-slate-800 dark:text-slate-100 mb-0">
            {{ selectedDomain?.domain_name }}
          </h5>
          <span
            class="badge text-[11px] font-semibold py-0.5 px-2.5 rounded-full"
            [class]="
              +selectedDomain?.status === 2
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                : +selectedDomain?.status === 1
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                : 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300'
            "
          >
            {{ displayDomainStatus(selectedDomain?.status) | translate }}
          </span>
        </div>
        <p class="text-xs text-slate-400 mb-0 mt-0.5">
          {{ "Custom domain configuration and status overview" | translate }}
        </p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeDomainModal()"></button>
  </div>

  <div class="modal-body p-4" *ngIf="selectedDomain">

    <!-- Card 1: Domain & SSL Status -->
    <div class="rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/30 p-3.5 mb-3">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="d-flex align-items-center gap-2">
          <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ "Primary URL" | translate }}:</span>
          <a
            [href]="'https://' + selectedDomain.domain_name"
            target="_blank"
            rel="noopener noreferrer"
            class="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400 d-inline-flex align-items-center hover:underline"
          >
            https://{{ selectedDomain.domain_name }}
            <i class="fi fi-rr-arrow-square-right text-[10px] ml-1.5"></i>
          </a>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-light border border-slate-200 dark:border-slate-700 text-xs py-1 px-2.5 d-inline-flex align-items-center"
          (click)="copyToClipboard('https://' + selectedDomain.domain_name, 'Domain URL')"
        >
          <i class="fi fi-rr-copy mr-1"></i>
          {{ "Copy URL" | translate }}
        </button>
      </div>

      <div class="row g-2 mt-2 pt-2.5 border-top border-slate-200/60 dark:border-slate-700/60">
        <div class="col-12 col-sm-6 d-flex align-items-center gap-2">
          <div class="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 d-flex align-items-center justify-content-center flex-shrink-0 text-xs">
            <i class="fi fi-rr-shield-check"></i>
          </div>
          <div>
            <div class="text-[11px] text-slate-400 uppercase font-semibold">{{ "SSL Security" | translate }}</div>
            <div class="text-xs font-semibold text-slate-700 dark:text-slate-200">{{ "Automatic HTTPS Enabled" | translate }}</div>
          </div>
        </div>

        <div class="col-12 col-sm-6 d-flex align-items-center gap-2">
          <div class="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 d-flex align-items-center justify-content-center flex-shrink-0 text-xs">
            <i class="fi fi-rr-network"></i>
          </div>
          <div>
            <div class="text-[11px] text-slate-400 uppercase font-semibold">{{ "CDN Routing" | translate }}</div>
            <div class="text-xs font-semibold text-slate-700 dark:text-slate-200">{{ "CloudFront Edge Network" | translate }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card 2: DNS Target (CNAME Record) -->
    <div class="rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 p-3.5 mb-3" *ngIf="selectedDomain.cf_dist_domain_name">
      <div class="d-flex align-items-center justify-content-between mb-1.5">
        <label class="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider mb-0">
          {{ "CNAME Target Host" | translate }}
        </label>
        <span class="text-[11px] text-slate-400 font-medium">{{ "DNS Record Destination" | translate }}</span>
      </div>
      <div class="input-group">
        <input
          type="text"
          class="form-control font-mono text-xs bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200"
          readonly
          [value]="selectedDomain.cf_dist_domain_name"
          (click)="selectAll($event)"
        />
        <button
          class="btn btn-outline-secondary d-inline-flex align-items-center px-3 text-xs font-semibold"
          type="button"
          (click)="copyToClipboard(selectedDomain.cf_dist_domain_name, 'CNAME Target')"
        >
          <i class="fi fi-rr-copy mr-1.5"></i>
          {{ "Copy Target" | translate }}
        </button>
      </div>
      <p class="text-[11px] text-slate-400 mb-0 mt-1.5">
        {{ "Point your domain or subdomain CNAME record to this target in your DNS provider (Cloudflare, GoDaddy, Namecheap, etc.)." | translate }}
      </p>
    </div>

    <!-- Card 3: Connected Subdomains Summary -->
    <div class="rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 p-3.5">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <label class="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider mb-0">
          {{ "Connected Subdomains" | translate }} ({{ getSubdomainList(selectedDomain).length }}/5)
        </label>
        <button
          *ngIf="+selectedDomain.setup_progress === 22 || +selectedDomain.setup_progress === 23"
          type="button"
          class="btn btn-link text-xs font-semibold p-0 text-indigo-600 dark:text-indigo-400 d-inline-flex align-items-center hover:underline"
          [disabled]="isDeletingDomain(selectedDomain)"
          (click)="closeDomainModal(); openManageSubdomains(selectedDomain)"
        >
          <i class="fi fi-rs-settings text-[11px] mr-1"></i>
          {{ "Manage" | translate }}
        </button>
      </div>

      <div class="d-flex flex-wrap gap-1.5" *ngIf="getSubdomainList(selectedDomain).length; else noSubdomainsInDetails">
        <ng-container *ngFor="let sub of getSubdomainList(selectedDomain)">
          <a
            *ngIf="isClickableSubdomain(sub) && !isDeletingDomain(selectedDomain); else nonClickableSubInDetails"
            [href]="buildSubdomainUrl(sub, selectedDomain.domain_name)"
            target="_blank"
            rel="noopener noreferrer"
            class="badge font-mono text-xs py-1 px-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 transition-colors border border-indigo-200/60 dark:border-indigo-800/60 d-inline-flex align-items-center gap-1.5 no-underline"
          >
            <span>{{ sub }}.{{ selectedDomain.domain_name }}</span>
            <i class="fi fi-rr-arrow-square-right text-[9px] opacity-70"></i>
          </a>
          <ng-template #nonClickableSubInDetails>
            <span class="badge font-mono text-xs py-1 px-2.5 rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
              {{ sub }}.{{ selectedDomain.domain_name }}
            </span>
          </ng-template>
        </ng-container>
      </div>
      <ng-template #noSubdomainsInDetails>
        <div class="text-xs text-slate-400 italic py-1">
          {{ "No subdomains configured yet." | translate }}
        </div>
      </ng-template>
    </div>

  </div>

  <div class="modal-footer border-top border-slate-100 dark:border-slate-700 pt-3">
    <button
      *ngIf="+selectedDomain?.setup_progress === 22 || +selectedDomain?.setup_progress === 23"
      type="button"
      class="btn btn-outline-primary btn-sm d-inline-flex align-items-center mr-auto"
      [disabled]="isDeletingDomain(selectedDomain)"
      (click)="closeDomainModal(); openManageSubdomains(selectedDomain)"
    >
      <i class="fi fi-rs-settings mr-1.5"></i>
      {{ "Manage Subdomains" | translate }}
    </button>
    <button type="button" class="btn btn-secondary btn-sm px-4" (click)="closeDomainModal()">
      {{ "Done" | translate }}
    </button>
  </div>
</ng-template>

<!-- Manage Subdomains Modal (Unified Single Modal) -->
<ng-template #domainManageSubdomainsModal let-modal>
  <div class="modal-header border-bottom border-slate-100 dark:border-slate-700 pb-3">
    <div class="d-flex align-items-center gap-2">
      <div class="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 d-flex align-items-center justify-content-center flex-shrink-0">
        <i class="fi fi-rs-settings text-sm"></i>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2">
          <h5 class="modal-title font-bold text-base text-slate-800 dark:text-slate-100 mb-0">{{ "Manage Subdomains" | translate }}</h5>
          <span
            class="badge text-[11px] font-semibold py-0.5 px-2 rounded-full"
            [class]="getSubdomainList(selectedDomain).length >= 5 ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300' : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'"
          >
            {{ getSubdomainList(selectedDomain).length }}/5 {{ "Subdomains" | translate }}
          </span>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-0 font-mono">
          {{ selectedDomain?.domain_name }}
        </p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeDomainModal()"></button>
  </div>

  <div class="modal-body p-3 p-md-4">
    <!-- 1. Inline Add Subdomain Card -->
    <div class="p-3 mb-4 rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-950/20">
      <label class="form-label font-semibold text-xs text-indigo-950 dark:text-indigo-200 mb-1.5 d-flex align-items-center justify-content-between">
        <span>{{ "Add New Subdomain" | translate }}</span>
        <span class="text-[11px] font-normal text-slate-500 dark:text-slate-400">({{ "e.g. shop, store, promo, @, *" | translate }})</span>
      </label>

      <div class="input-group">
        <input
          type="text"
          class="form-control text-sm"
          [(ngModel)]="userDomainSubdomain"
          [disabled]="isVerifyingDomainSubdomain || getSubdomainList(selectedDomain).length >= 5"
          placeholder="shop"
          (keyup.enter)="proceedVerifySubdomain()"
        />
        <span class="input-group-text font-mono text-xs text-slate-600 bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700">
          .{{ selectedDomain?.domain_name }}
        </span>
        <button
          type="button"
          class="btn btn-primary btn-sm px-3 d-inline-flex align-items-center gap-1.5"
          [disabled]="isVerifyingDomainSubdomain || !userDomainSubdomain?.trim() || getSubdomainList(selectedDomain).length >= 5"
          (click)="proceedVerifySubdomain()"
        >
          <span *ngIf="isVerifyingDomainSubdomain" class="spinner-border spinner-border-sm" role="status"></span>
          <i class="fi fi-rs-plus-small" *ngIf="!isVerifyingDomainSubdomain"></i>
          <span>{{ (isVerifyingDomainSubdomain ? "Verifying..." : "Add & Verify") | translate }}</span>
        </button>
      </div>

      <div *ngIf="getSubdomainList(selectedDomain).length >= 5" class="mt-2 text-xs text-amber-600 dark:text-amber-400 d-flex align-items-center gap-1">
        <i class="fi fi-rr-info"></i>
        <span>{{ "Maximum limit of 5 subdomains reached for this domain." | translate }}</span>
      </div>

      <!-- Quick CNAME DNS reminder -->
      <div class="mt-2 text-[11px] text-slate-500 dark:text-slate-400 d-flex align-items-center gap-1.5">
        <i class="fi fi-rr-bulb text-amber-500"></i>
        <span>{{ "Make sure to add a matching CNAME record in your domain registrar DNS settings." | translate }}</span>
      </div>
    </div>

    <!-- 2. Active Subdomains List -->
    <div>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h6 class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0">
          {{ "Connected Subdomains" | translate }}
        </h6>
        <span class="text-xs text-slate-400">{{ getSubdomainList(selectedDomain).length }} {{ "active" | translate }}</span>
      </div>

      <div *ngIf="getSubdomainList(selectedDomain).length; else emptySubsInModal" class="d-flex flex-column gap-2">
        <div
          *ngFor="let sub of getSubdomainList(selectedDomain)"
          class="d-flex align-items-center justify-content-between p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-all"
        >
          <div class="d-flex align-items-center gap-2 min-w-0">
            <div class="w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-700 d-flex align-items-center justify-content-center text-slate-500 dark:text-slate-300 flex-shrink-0">
              <i class="fi fi-rr-globe text-xs"></i>
            </div>
            <div class="min-w-0">
              <a
                *ngIf="isClickableSubdomain(sub)"
                [href]="buildSubdomainUrl(sub, selectedDomain?.domain_name)"
                target="_blank"
                rel="noopener noreferrer"
                class="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline d-inline-flex align-items-center gap-1 text-decoration-none"
              >
                <span>{{ sub }}.{{ selectedDomain?.domain_name }}</span>
                <i class="fi fi-rr-arrow-small-right text-[10px]"></i>
              </a>
              <span *ngIf="!isClickableSubdomain(sub)" class="font-mono text-sm font-semibold text-slate-700 dark:text-slate-300">
                {{ sub }}.{{ selectedDomain?.domain_name }}
              </span>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 text-[10px] font-semibold py-0.5 px-2 rounded-full border border-emerald-200 dark:border-emerald-800">
              {{ "Live" | translate }}
            </span>
            <button
              type="button"
              class="btn-domain-sub-delete"
              title="{{ 'Delete Subdomain' | translate }}"
              (click)="deleteSubdomain(sub)"
            >
              <i class="fi fi-rs-trash"></i>
            </button>
          </div>
        </div>
      </div>

      <ng-template #emptySubsInModal>
        <div class="text-center py-4 px-3 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 text-slate-400">
          <i class="fi fi-rr-folder text-2xl mb-1 d-block text-slate-300 dark:text-slate-600"></i>
          <p class="text-xs mb-0">{{ "No custom subdomains connected yet. Add your first subdomain above." | translate }}</p>
        </div>
      </ng-template>
    </div>
  </div>

  <div class="modal-footer border-top border-slate-100 dark:border-slate-700 pt-2 pb-2">
    <button type="button" class="btn btn-light btn-sm" (click)="closeDomainModal()">
      <span>{{ "Done" | translate }}</span>
    </button>
  </div>
</ng-template>

<!-- Add / Connect Custom Domain Modal (Single Existing Domain Integration Flow) -->
<ng-template #domainAddModal let-modal>
  <div class="modal-header border-bottom border-slate-100 dark:border-slate-700 pb-3">
    <div class="d-flex align-items-center gap-2.5">
      <div class="domain-modal-icon-wrap rounded-xl bg-gradient-to-tr from-indigo-600 to-indigo-500 text-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm">
        <i class="fi fi-rr-globe text-base"></i>
      </div>
      <div>
        <h5 class="modal-title font-bold text-base text-slate-800 dark:text-slate-100 mb-0.5">
          {{ "Connect Your Custom Domain" | translate }}
        </h5>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-0">
          {{ "Integrate a domain you already purchased from your domain provider." | translate }}
        </p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeDomainModal()"></button>
  </div>

  <div class="modal-body p-3 p-md-4">
    <!-- 1. Domain Input with On-Demand Check Availability / Status Button -->
    <div class="mb-3">
      <label class="form-label font-semibold text-xs text-slate-700 dark:text-slate-300 mb-1.5 d-flex align-items-center justify-content-between">
        <span>{{ "Domain Name" | translate }} <span class="text-red-500">*</span></span>
        <span class="text-[11px] font-normal text-slate-400">({{ "e.g. yourbrand.com" | translate }})</span>
      </label>

      <div class="input-group">
        <span class="input-group-text font-mono text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700">
          https://
        </span>
        <input
          type="text"
          class="form-control text-sm font-mono"
          [(ngModel)]="addDomainInput"
          [disabled]="isSubmittingAddDomain || isCheckingAddDomain"
          (ngModelChange)="onAddDomainInputChange()"
          (keyup.enter)="checkExistingDomainAvailability()"
          placeholder="yourbrand.com"
        />
        <button
          type="button"
          class="btn btn-outline-secondary px-3 d-inline-flex align-items-center gap-1.5 font-medium text-xs"
          [disabled]="isCheckingAddDomain || isSubmittingAddDomain || !addDomainInput?.trim()"
          (click)="checkExistingDomainAvailability()"
          title="{{ 'Check if domain is registered and available for integration' | translate }}"
        >
          <span *ngIf="isCheckingAddDomain" class="spinner-border spinner-border-sm" role="status"></span>
          <i class="fi fi-rr-search" *ngIf="!isCheckingAddDomain"></i>
          <span>{{ (isCheckingAddDomain ? "Checking..." : "Check Status") | translate }}</span>
        </button>
      </div>

      <!-- Live URL Preview -->
      <div class="mt-1.5 d-flex align-items-center justify-content-between" *ngIf="addDomainInput?.trim()">
        <span class="text-[11px] text-slate-400 font-mono">
          {{ "Store URL Preview" | translate }}: <strong class="text-indigo-600 dark:text-indigo-400">https://{{ cleanDomainInput(addDomainInput) }}</strong>
        </span>
      </div>
    </div>

    <!-- 2. Dynamic Real-Time Availability & Validation Feedback -->
    <!-- State: Verified / Registered (Ready to connect) -->
    <div *ngIf="addDomainStatus === 'registered'" class="p-3 mb-3 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30">
      <div class="d-flex align-items-center gap-2.5">
        <div class="w-7 h-7 rounded-full bg-emerald-500 text-white d-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fi fi-rr-check text-xs"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-1.5 mb-0.5">
            <span class="font-bold text-sm text-emerald-900 dark:text-emerald-200 font-mono">{{ addDomainCheckedName }}</span>
            <span class="badge bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 text-[10px] font-semibold py-0.5 px-2 rounded-full">
              {{ "Domain Verified" | translate }}
            </span>
          </div>
          <p class="text-xs text-emerald-700 dark:text-emerald-400 mb-0">
            {{ "Domain is registered and ready to integrate with your store." | translate }}
          </p>
        </div>
      </div>
    </div>

    <!-- State: Unregistered / Typo Warning -->
    <div *ngIf="addDomainStatus === 'unregistered'" class="p-3 mb-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30">
      <div class="d-flex align-items-start gap-2.5">
        <div class="w-7 h-7 rounded-full bg-amber-500 text-white d-flex align-items-center justify-content-center flex-shrink-0 mt-0.5">
          <i class="fi fi-rr-exclamation text-xs"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-1.5 mb-0.5">
            <span class="font-bold text-sm text-amber-900 dark:text-amber-200 font-mono">{{ addDomainCheckedName }}</span>
            <span class="badge bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300 text-[10px] font-semibold py-0.5 px-2 rounded-full">
              {{ "Not Found on DNS" | translate }}
            </span>
          </div>
          <p class="text-xs text-amber-800 dark:text-amber-300 mb-0 leading-relaxed">
            {{ "This domain was not found in the global DNS registry. Please ensure you have already purchased and registered this domain with a registrar (e.g. GoDaddy, Namecheap, Cloudflare) before connecting." | translate }}
          </p>
        </div>
      </div>
    </div>

    <!-- State: Error / Already Taken -->
    <div *ngIf="addDomainError && addDomainStatus !== 'unregistered'" class="mb-3 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 d-flex align-items-center gap-2">
      <i class="fi fi-rr-exclamation flex-shrink-0"></i>
      <span>{{ addDomainError }}</span>
    </div>
  </div>

  <div class="modal-footer border-top border-slate-100 dark:border-slate-700 pt-2 pb-2 d-flex justify-content-between">
    <button type="button" class="btn btn-light btn-sm" (click)="closeDomainModal()">
      <span>{{ "Cancel" | translate }}</span>
    </button>
    <button
      type="button"
      class="btn btn-primary btn-sm d-inline-flex align-items-center font-medium"
      [disabled]="isSubmittingAddDomain || !addDomainInput?.trim()"
      (click)="submitAddDomain()"
    >
      <span *ngIf="isSubmittingAddDomain" class="spinner-border spinner-border-sm mr-1" role="status"></span>
      <i class="fi fi-rr-check mr-1" *ngIf="!isSubmittingAddDomain"></i>
      <span>{{ (isSubmittingAddDomain ? "Connecting..." : "Connect Domain") | translate }}</span>
    </button>
  </div>
</ng-template>

<!-- Step 11 Action Modal: Add CNAME Verification Record -->
<ng-template #guideModal001 let-modal>
  <div class="modal-header border-bottom border-slate-100 dark:border-slate-700 pb-3">
    <div class="d-flex align-items-center gap-2.5">
      <div class="domain-modal-icon-wrap rounded-xl bg-gradient-to-tr from-amber-500 to-amber-600 text-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm">
        <i class="fi fi-rr-shield-check text-base"></i>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2">
          <h5 class="modal-title font-bold text-base text-slate-800 dark:text-slate-100 mb-0">
            {{ "Add CNAME Verification Record" | translate }}
          </h5>
          <span class="badge bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 text-[10px] font-semibold py-0.5 px-2 rounded-full">
            {{ "Step 1 of 2" | translate }}
          </span>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-0 font-mono">
          {{ selectedDomain?.domain_name }}
        </p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeDomainModal()"></button>
  </div>

  <div class="modal-body p-3 p-md-4">
    <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">
      {{ "To issue an SSL certificate for your custom domain, please add the following CNAME record to your domain DNS settings at your registrar (e.g. GoDaddy, Namecheap, Cloudflare, Route53)." | translate }}
    </p>

    <!-- DNS Record Data Card -->
    <div class="p-3 mb-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
      <div class="mb-3">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <label class="font-semibold text-xs text-slate-700 dark:text-slate-300 mb-0">
            1. {{ "CNAME Name / Host" | translate }}
          </label>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 text-xs text-indigo-600 dark:text-indigo-400 text-decoration-none d-inline-flex align-items-center gap-1"
            (click)="copyToClipboard(selectedDomain.cnameName, 'CNAME Name')"
          >
            <i class="fi fi-rr-copy text-xs"></i>
            <span>{{ "Copy Name" | translate }}</span>
          </button>
        </div>
        <div class="input-group">
          <input
            type="text"
            readonly
            class="form-control text-sm font-mono bg-white dark:bg-slate-800"
            (focus)="selectAll($event)"
            [value]="selectedDomain.cnameName || ''"
          />
          <span *ngIf="selectedDomain.cnameDomainName" class="input-group-text font-mono text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700">
            .{{ selectedDomain.cnameDomainName }}
          </span>
        </div>
      </div>

      <div>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <label class="font-semibold text-xs text-slate-700 dark:text-slate-300 mb-0">
            2. {{ "CNAME Value / Points To" | translate }}
          </label>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 text-xs text-indigo-600 dark:text-indigo-400 text-decoration-none d-inline-flex align-items-center gap-1"
            (click)="copyToClipboard(selectedDomain.cnameValue, 'CNAME Value')"
          >
            <i class="fi fi-rr-copy text-xs"></i>
            <span>{{ "Copy Value" | translate }}</span>
          </button>
        </div>
        <div class="input-group">
          <input
            type="text"
            readonly
            class="form-control text-sm font-mono bg-white dark:bg-slate-800"
            (focus)="selectAll($event)"
            [value]="selectedDomain.cnameValue || ''"
          />
        </div>
      </div>
    </div>

    <div class="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-xs text-amber-800 dark:text-amber-300 d-flex align-items-start gap-2">
      <i class="fi fi-rr-info flex-shrink-0 mt-0.5"></i>
      <span>
        {{ "After adding this record in your domain provider DNS settings, click the button below. DNS verification may take a few minutes to propagate." | translate }}
      </span>
    </div>
  </div>

  <div class="modal-footer border-top border-slate-100 dark:border-slate-700 pt-2 pb-2 d-flex justify-content-between">
    <button type="button" class="btn btn-light btn-sm" (click)="closeDomainModal()">
      <span>{{ "Cancel" | translate }}</span>
    </button>
    <button
      type="button"
      class="btn btn-primary btn-sm d-inline-flex align-items-center font-medium"
      [disabled]="isVerifyingCname"
      (click)="cnameSubmitted(selectedDomain)"
    >
      <i class="fa fa-spinner fa-spin mr-1" *ngIf="isVerifyingCname"></i>
      <i class="fi fi-rr-check mr-1" *ngIf="!isVerifyingCname"></i>
      <span>{{ (isVerifyingCname ? "Verifying..." : "Verify DNS Record") | translate }}</span>
    </button>
  </div>
</ng-template>

<!-- Step 21 Action Modal: Point Domain & Verify Subdomain -->
<ng-template #guideModal002 let-modal>
  <div class="modal-header border-bottom border-slate-100 dark:border-slate-700 pb-3">
    <div class="d-flex align-items-center gap-2.5">
      <div class="domain-modal-icon-wrap rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-600 text-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm">
        <i class="fi fi-rr-link text-base"></i>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2">
          <h5 class="modal-title font-bold text-base text-slate-800 dark:text-slate-100 mb-0">
            {{ "Point DNS Record & Verify Subdomain" | translate }}
          </h5>
          <span class="badge bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 text-[10px] font-semibold py-0.5 px-2 rounded-full">
            {{ "Step 2 of 2" | translate }}
          </span>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-0 font-mono">
          {{ selectedDomain?.domain_name }}
        </p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="closeDomainModal()"></button>
  </div>

  <div class="modal-body p-3 p-md-4">
    <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">
      {{ "Add a CNAME or ALIAS record pointing your desired subdomain to our CloudFront network." | translate }}
    </p>

    <!-- CloudFront Target Box -->
    <div class="p-3 mb-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <label class="font-semibold text-xs text-slate-700 dark:text-slate-300 mb-0">
          {{ "CloudFront Target Value (Points To)" | translate }}
        </label>
        <button
          type="button"
          class="btn btn-link btn-sm p-0 text-xs text-indigo-600 dark:text-indigo-400 text-decoration-none d-inline-flex align-items-center gap-1"
          (click)="copyToClipboard(selectedDomain.cf_dist_domain_name, 'Target Value')"
        >
          <i class="fi fi-rr-copy text-xs"></i>
          <span>{{ "Copy Target" | translate }}</span>
        </button>
      </div>
      <div class="input-group">
        <input
          type="text"
          readonly
          class="form-control text-sm font-mono bg-white dark:bg-slate-800"
          (focus)="selectAll($event)"
          [value]="selectedDomain.cf_dist_domain_name || ''"
        />
      </div>
    </div>

    <!-- Subdomain Options and Input -->
    <div class="p-3 mb-3 rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/30 dark:bg-indigo-950/20">
      <label class="form-label font-semibold text-xs text-indigo-950 dark:text-indigo-200 mb-1.5 d-flex align-items-center justify-content-between">
        <span>{{ "Enter Subdomain You Configured in DNS" | translate }} <span class="text-red-500">*</span></span>
        <span class="text-[11px] font-normal text-slate-500">({{ "e.g. shop, store, @, *" | translate }})</span>
      </label>

      <div class="input-group mb-2">
        <input
          type="text"
          class="form-control text-sm font-mono"
          [(ngModel)]="stepSubdomainInput"
          [disabled]="isVerifyingStepSubdomain"
          placeholder="shop"
          (keyup.enter)="verifyStep21Subdomain()"
        />
        <span class="input-group-text font-mono text-xs text-slate-600 bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700">
          .{{ selectedDomain?.domain_name }}
        </span>
      </div>

      <!-- Helper examples -->
      <div class="d-flex flex-wrap gap-1 text-[11px] text-slate-500 dark:text-slate-400 align-items-center">
        <span>{{ "Common options:" | translate }}</span>
        <button type="button" class="btn btn-outline-secondary btn-xs py-0 px-1.5 text-[11px]" (click)="stepSubdomainInput = 'shop'">shop</button>
        <button type="button" class="btn btn-outline-secondary btn-xs py-0 px-1.5 text-[11px]" (click)="stepSubdomainInput = 'store'">store</button>
        <button type="button" class="btn btn-outline-secondary btn-xs py-0 px-1.5 text-[11px]" (click)="stepSubdomainInput = '@'">@ (root)</button>
        <button type="button" class="btn btn-outline-secondary btn-xs py-0 px-1.5 text-[11px]" (click)="stepSubdomainInput = '*'">* (wildcard)</button>
      </div>
    </div>
  </div>

  <div class="modal-footer border-top border-slate-100 dark:border-slate-700 pt-2 pb-2 d-flex justify-content-between">
    <button type="button" class="btn btn-light btn-sm" (click)="closeDomainModal()">
      <span>{{ "Cancel" | translate }}</span>
    </button>
    <button
      type="button"
      class="btn btn-primary btn-sm d-inline-flex align-items-center font-medium"
      [disabled]="isVerifyingStepSubdomain || !stepSubdomainInput?.trim()"
      (click)="verifyStep21Subdomain()"
    >
      <i class="fa fa-spinner fa-spin mr-1" *ngIf="isVerifyingStepSubdomain"></i>
      <i class="fi fi-rr-check mr-1" *ngIf="!isVerifyingStepSubdomain"></i>
      <span>{{ (isVerifyingStepSubdomain ? "Verifying..." : "Verify Subdomain & Finish") | translate }}</span>
    </button>
  </div>
</ng-template>
